import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { ArrowLeft, Cookie, Shield, Info, Users, FileText, ExternalLink } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { i18n } from '@/utils/i18n';

/**
 * Comprehensive Cookie Policy Page
 * 
 * This page provides a detailed, GDPR-compliant cookie policy that explains:
 * - What cookies are and how they're used
 * - Types of cookies we use with specific examples
 * - User rights and control options
 * - Third-party cookie information
 * - Opt-out instructions
 * - Legal basis for cookie processing
 */
const CookiePolicyPage: React.FC = () => {
  const navigate = useNavigate();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {i18n.__('cookiePolicy.back')}
          </Button>
          <div className="flex items-center gap-3">
            <Cookie className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{i18n.__('cookiePolicy.title')}</h1>
              <p className="text-gray-600">{i18n.__('cookiePolicy.lastUpdated', { date: new Date().toLocaleDateString() })}</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <Card className="mb-8 border-l-4 border-l-primary">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">{i18n.__('cookiePolicy.manage')}</h3>
                <p className="text-gray-600">
                  {i18n.__('cookiePolicy.manageDesc')}
                </p>
              </div>
              <div className="flex gap-3">
                <Link to="/cookie-settings">
                  <Button className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    {i18n.__('cookiePolicy.cookieSettings')}
                  </Button>
                </Link>
                <Link to="/privacy-policy">
                  <Button variant="outline" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {i18n.__('cookiePolicy.privacyPolicy')}
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              {i18n.__('cookiePolicy.toc')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-2">
              {[
                { id: 'what-are-cookies', title: i18n.__('cookiePolicy.toc.what') },
                { id: 'how-we-use-cookies', title: i18n.__('cookiePolicy.toc.how') },
                { id: 'types-of-cookies', title: i18n.__('cookiePolicy.toc.types') },
                { id: 'specific-cookies', title: i18n.__('cookiePolicy.toc.specific') },
                { id: 'third-party-cookies', title: i18n.__('cookiePolicy.toc.thirdParty') },
                { id: 'your-choices', title: i18n.__('cookiePolicy.toc.choices') },
                { id: 'legal-basis', title: i18n.__('cookiePolicy.toc.legal') },
                { id: 'contact-us', title: i18n.__('cookiePolicy.toc.contact') }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-primary hover:text-primary/80 hover:underline transition-colors p-1"
                >
                  {item.title}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1: What Are Cookies */}
          <Card id="what-are-cookies">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.__('cookiePolicy.what.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                {i18n.__('cookiePolicy.what.desc')}
              </p>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-l-blue-500">
                <h4 className="font-semibold mb-2">{i18n.__('cookiePolicy.what.keyPoints')}</h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>{i18n.__('cookiePolicy.what.point1')}</li>
                  <li>{i18n.__('cookiePolicy.what.point2')}</li>
                  <li>{i18n.__('cookiePolicy.what.point3')}</li>
                  <li>{i18n.__('cookiePolicy.what.point4')}</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: How We Use Cookies */}
          <Card id="how-we-use-cookies">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.__('cookiePolicy.how.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                {i18n.__('cookiePolicy.how.desc')}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-green-800">{i18n.__('cookiePolicy.how.essential')}</h4>
                  <ul className="text-sm space-y-1">
                    <li>• {i18n.__('cookiePolicy.how.essential1')}</li>
                    <li>• {i18n.__('cookiePolicy.how.essential2')}</li>
                    <li>• {i18n.__('cookiePolicy.how.essential3')}</li>
                    <li>• {i18n.__('cookiePolicy.how.essential4')}</li>
                  </ul>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-blue-800">{i18n.__('cookiePolicy.how.analytics')}</h4>
                  <ul className="text-sm space-y-1">
                    <li>• {i18n.__('cookiePolicy.how.analytics1')}</li>
                    <li>• {i18n.__('cookiePolicy.how.analytics2')}</li>
                    <li>• {i18n.__('cookiePolicy.how.analytics3')}</li>
                    <li>• {i18n.__('cookiePolicy.how.analytics4')}</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Types of Cookies */}
          <Card id="types-of-cookies">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.__('cookiePolicy.types.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {[
                  {
                    icon: Shield,
                    title: i18n.__('cookiePolicy.types.essential'),
                    description: i18n.__('cookiePolicy.types.essentialDesc'),
                    color: "green",
                    examples: i18n.__('cookiePolicy.types.essentialEx').split(', ')
                  },
                  {
                    icon: Users,
                    title: i18n.__('cookiePolicy.types.functional'),
                    description: i18n.__('cookiePolicy.types.functionalDesc'),
                    color: "blue", 
                    examples: i18n.__('cookiePolicy.types.functionalEx').split(', ')
                  },
                  {
                    icon: Info,
                    title: i18n.__('cookiePolicy.types.analytics'),
                    description: i18n.__('cookiePolicy.types.analyticsDesc'),
                    color: "purple",
                    examples: i18n.__('cookiePolicy.types.analyticsEx').split(', ')
                  },
                  {
                    icon: ExternalLink,
                    title: i18n.__('cookiePolicy.types.marketing'),
                    description: i18n.__('cookiePolicy.types.marketingDesc'),
                    color: "orange",
                    examples: i18n.__('cookiePolicy.types.marketingEx').split(', ')
                  }
                ].map((type, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <type.icon className={`h-6 w-6 mt-1 text-${type.color}-600`} />
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg mb-2">{type.title}</h4>
                        <p className="text-gray-600 mb-3">{type.description}</p>
                        <div className="bg-gray-50 p-3 rounded">
                          <h5 className="font-medium mb-1">Examples:</h5>
                          <div className="grid grid-cols-2 gap-1 text-sm">
                            {type.examples.map((example, i) => (
                              <span key={i} className="text-gray-600">• {example}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Section 4: Specific Cookies Table */}
          <Card id="specific-cookies">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.__('cookiePolicy.specific.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 p-3 text-left">{i18n.__('cookiePolicy.specific.name')}</th>
                      <th className="border border-gray-300 p-3 text-left">{i18n.__('cookiePolicy.specific.purpose')}</th>
                      <th className="border border-gray-300 p-3 text-left">{i18n.__('cookiePolicy.specific.duration')}</th>
                      <th className="border border-gray-300 p-3 text-left">{i18n.__('cookiePolicy.specific.type')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: "vaiu_session", purpose: "Maintains your login session", duration: "Session", type: "Essential" },
                      { name: "vaiu_auth_token", purpose: "Authentication and security", duration: "30 days", type: "Essential" },
                      { name: "vaiu_language", purpose: "Remembers your language preference", duration: "1 year", type: "Functional" },
                      { name: "vaiu_theme", purpose: "Stores your UI theme preference", duration: "1 year", type: "Functional" },
                      { name: "_ga", purpose: "Google Analytics - distinguishes users", duration: "2 years", type: "Analytics" },
                      { name: "_gid", purpose: "Google Analytics - distinguishes users", duration: "24 hours", type: "Analytics" },
                      { name: "vaiu_marketing_consent", purpose: "Tracks marketing consent status", duration: "1 year", type: "Marketing" }
                    ].map((cookie, index) => (
                      <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="border border-gray-300 p-3 font-mono text-sm">{cookie.name}</td>
                        <td className="border border-gray-300 p-3">{cookie.purpose}</td>
                        <td className="border border-gray-300 p-3">{cookie.duration}</td>
                        <td className="border border-gray-300 p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            cookie.type === 'Essential' ? 'bg-green-100 text-green-800' :
                            cookie.type === 'Functional' ? 'bg-blue-100 text-blue-800' :
                            cookie.type === 'Analytics' ? 'bg-purple-100 text-purple-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {cookie.type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Section 5: Third-Party Cookies */}
          <Card id="third-party-cookies">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.__('cookiePolicy.thirdParty.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                {i18n.__('cookiePolicy.thirdParty.desc')}
              </p>
              <div className="space-y-4">
                {[
                  {
                    name: i18n.__('cookiePolicy.thirdParty.google'),
                    purpose: i18n.__('cookiePolicy.thirdParty.googleDesc'),
                    optOut: i18n.__('cookiePolicy.thirdParty.googleOptOut'),
                    policy: "https://policies.google.com/privacy"
                  },
                  {
                    name: i18n.__('cookiePolicy.thirdParty.stripe'),
                    purpose: i18n.__('cookiePolicy.thirdParty.stripeDesc'),
                    optOut: i18n.__('cookiePolicy.thirdParty.stripeOptOut'),
                    policy: "https://stripe.com/privacy"
                  },
                  {
                    name: i18n.__('cookiePolicy.thirdParty.firebase'),
                    purpose: i18n.__('cookiePolicy.thirdParty.firebaseDesc'),
                    optOut: i18n.__('cookiePolicy.thirdParty.firebaseOptOut'),
                    policy: "https://policies.google.com/privacy"
                  }
                ].map((service, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-2">{service.name}</h4>
                    <p className="text-gray-600 mb-2">{service.purpose}</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <span><strong>Opt-out:</strong> {service.optOut}</span>
                      <a
                        href={service.policy}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        {i18n.__('cookiePolicy.privacyPolicy')} <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Section 6: Your Choices */}
          <Card id="your-choices">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.__('cookiePolicy.choices.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                {i18n.__('cookiePolicy.choices.desc')}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">{i18n.__('cookiePolicy.choices.ourSettings')}</h4>
                  <p className="text-sm mb-3">
                    {i18n.__('cookiePolicy.choices.ourSettingsDesc')}
                  </p>
                  <Link to="/cookie-settings">
                    <Button size="sm" className="w-full">
                      {i18n.__('cookiePolicy.choices.openSettings')}
                    </Button>
                  </Link>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">{i18n.__('cookiePolicy.choices.browserSettings')}</h4>
                  <p className="text-sm mb-3">
                    {i18n.__('cookiePolicy.choices.browserSettingsDesc')}
                  </p>
                  <ul className="text-xs space-y-1">
                    <li>• Chrome: Settings → Privacy → Cookies</li>
                    <li>• Firefox: Settings → Privacy → Cookies</li>
                    <li>• Safari: Preferences → Privacy</li>
                    <li>• Edge: Settings → Privacy → Cookies</li>
                  </ul>
                </div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-l-yellow-500">
                <h4 className="font-semibold mb-2">{i18n.__('cookiePolicy.choices.importantNote')}</h4>
                <p className="text-sm">
                  {i18n.__('cookiePolicy.choices.importantNoteDesc')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 7: Legal Basis */}
          <Card id="legal-basis">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.__('cookiePolicy.legal.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                {i18n.__('cookiePolicy.legal.desc')}
              </p>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Shield className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">{i18n.__('cookiePolicy.legal.essential')}</h4>
                    <p className="text-sm text-gray-600">
                      {i18n.__('cookiePolicy.legal.essentialDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Users className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">{i18n.__('cookiePolicy.legal.functional')}</h4>
                    <p className="text-sm text-gray-600">
                      {i18n.__('cookiePolicy.legal.functionalDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-purple-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">{i18n.__('cookiePolicy.legal.analytics')}</h4>
                    <p className="text-sm text-gray-600">
                      {i18n.__('cookiePolicy.legal.analyticsDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <ExternalLink className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">{i18n.__('cookiePolicy.legal.marketing')}</h4>
                    <p className="text-sm text-gray-600">
                      {i18n.__('cookiePolicy.legal.marketingDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 8: Contact */}
          <Card id="contact-us">
            <CardHeader>
              <CardTitle className="text-2xl">{i18n.__('cookiePolicy.contact.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                {i18n.__('cookiePolicy.contact.desc')}
              </p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><strong>{i18n.__('cookiePolicy.contact.email')}</strong></p>
                <p><strong>{i18n.__('cookiePolicy.contact.dpo')}</strong></p>
                <p><strong>{i18n.__('cookiePolicy.contact.address')}</strong></p>
                <p><strong>{i18n.__('cookiePolicy.contact.response')}</strong></p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer Actions */}
        <div className="mt-12 flex flex-col md:flex-row gap-4 justify-center items-center">
          <Link to="/cookie-settings">
            <Button size="lg" className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {i18n.__('cookiePolicy.footer.manage')}
            </Button>
          </Link>
          <Link to="/privacy-policy">
            <Button variant="outline" size="lg" className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {i18n.__('cookiePolicy.footer.viewPrivacy')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage; 